import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import isBetween from 'dayjs/plugin/isBetween'
import { useBalanceOperationList } from './hooks'
import { CButton, CCardBody } from '@coreui/react-pro'
import { path, role } from '../../../../libs'
import {
  Badge,
  Button,
  DatePicker,
  Input,
  InputRef,
  notification,
  Space,
  Table,
  TimeRangePickerProps,
} from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { FilterConfirmProps, FilterValue } from 'antd/es/table/interface'
import { BalanceOperation } from './types'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import { formatDigits } from '../../../../libs/utils'
import { useUnit } from 'effector-react/effector-react.mjs'
import { $isSuccessCreate } from '../../widgets/CreateDepositHeadwayForm/model'
import { NotificationPlacement } from 'antd/es/notification/interface'

type DataIndex = keyof BalanceOperation

export const BalanceOperationList = () => {
  const {
    balanceOperations,
    totalCountOfBalanceOperations,
    loading,
    onChangeBalanceOperationList,
  } = useBalanceOperationList()
  const userRole = useStore($userRole)
  const isSuccessCreate = useUnit($isSuccessCreate)

  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const [api, contextHolder] = notification.useNotification()

  const openNotification = (
    placement: NotificationPlacement,
    type: string,
    message: string,
  ) => {
    api.success({
      message: type,
      description: message,
      placement,
    })
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    currentSearchText,
    currentSearchedColumn,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    currentSearchText?: string
    currentSearchedColumn?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentSearchText) {
      setSearchText(currentSearchText)
    }
    if (currentSearchedColumn) {
      setSearchedColumn(currentSearchedColumn)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }
    if (filters) {
      setFiltersState(filters)
    }

    onChangeBalanceOperationList({
      limit: currentPageSize || pageSize,
      page: currentPage || page,
      orderBy: currentOrderBy || orderBy,
      orderDirection: currentOrderDirection || orderDirection,
      searchText: currentSearchText || searchText,
      searchedColumn: currentSearchedColumn || searchedColumn,
      filters: {},
    })
  }
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
    handleTableChange({
      currentSearchText: selectedKeys[0],
      currentSearchedColumn: dataIndex,
    })
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })
  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ]

  // TODO - доработать фильтрацию дат после доработки бэка
  const getColumnSearchDateProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          style={{ marginBottom: 8, display: 'block' }}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e ? [e] : [])}
          presets={rangePresets}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      dayjs.extend(isBetween)
      return record[dataIndex]
        ? dayjs(record[dataIndex]).isBetween(dayjs(value[0]), dayjs(value[1]))
        : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<BalanceOperation> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps('id'),
      sorter: true,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      sorter: true,
      defaultSortOrder: 'descend',
      ...getColumnSearchProps('createdAt'),
    },
    {
      title: 'Client',
      sorter: true,
      ...getColumnSearchProps('clientId'),
      render: (item: BalanceOperation) => (
        <div>
          {item.client?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a href={path.clients.details(item.clientId)} target="_blank">
                {item.clientId}
              </a>
            </Badge>
          ) : (
            <a href={path.clients.details(item.clientId)} target="_blank">
              {item.clientId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'To',
      dataIndex: 'info',
      sorter: true,
      ...getColumnSearchProps('info'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      sorter: true,
      ...getColumnSearchProps('amount'),
      render: (amount: string) => formatDigits(Number(amount)),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      render: (comment: number) => comment,
    },
    {
      title: 'Internal comment',
      dataIndex: 'internalComment',
      render: (comment: number) => comment,
    },
  ]

  const csvContent = balanceOperations
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const restrictedRoles = new Set([
    role.clientSupport.supportAgent(),
    role.clientSupport.supportManager(),
  ])

  useEffect(() => {
    if (isSuccessCreate) {
      openNotification('topRight', 'Success', 'Payment successfully created')
    }
  }, [isSuccessCreate])

  return (
    <CCardBody>
      {contextHolder}
      <CButton
        color="primary"
        className="mb-2"
        hidden={
          Array.isArray(userRole) &&
          userRole.some((role) => restrictedRoles.has(role))
        }
        href={path.billing.balanceOperation.create()}
      >
        Create balance operation
      </CButton>
      <Table
        dataSource={balanceOperations}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="BalanceOperationId"
        pagination={{
          showSizeChanger: true,
          total: totalCountOfBalanceOperations,
        }}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current,
            //@ts-ignore
            currentPageSize: pagination.pageSize,
            //@ts-ignore
            currentOrderDirection: sorter.order,
            //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      ></Table>
    </CCardBody>
  )
}
